import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Admin from "./pages/Admin";
import DeleteAccount from "./pages/DeleteAccount";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import BrandGuide from "./pages/BrandGuide";
import NotFound from "./pages/NotFound";
import { toast } from "sonner";

const queryClient = new QueryClient();

// Global error boundary for unhandled promise rejections
function GlobalErrorHandler({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('[App] Unhandled promise rejection:', event.reason);
      
      // Prevent default browser error handling
      event.preventDefault();
      
      // Show user-friendly message for common errors
      const message = event.reason?.message || String(event.reason);
      const messageLower = message.toLowerCase();
      
      if (messageLower.includes('network') || messageLower.includes('fetch')) {
        toast.error('Network error. Please check your connection.');
      } else if (messageLower.includes('timeout')) {
        toast.error('Request timed out. Please try again.');
      } else if (messageLower.includes('chunk') || messageLower.includes('module')) {
        // Likely a lazy loading error - suggest refresh
        toast.error('Loading error. Please refresh the page.');
      } else {
        // Log but don't show generic errors to user unless it's critical
        console.error('[App] Suppressed error toast for:', message);
      }
    };

    const handleError = (event: ErrorEvent) => {
      console.error('[App] Global error:', event.error);
      // Let React's error boundary handle component errors
    };

    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <GlobalErrorHandler>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              } />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/brand" element={<BrandGuide />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              } />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </GlobalErrorHandler>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
